import { useState, useEffect } from "react";
import { useAsyncValue } from "react-router";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dropdown } from "primereact/dropdown";
import { Chip } from "primereact/chip";
import countriesList from "i18n-iso-countries";
import countriesJson from "i18n-iso-countries/langs/en.json";
import "../../styles/components/mapOptions.scss";

const MapOptions = ({setFarmsSelected, selectDisplay, setselectDisplay}) => {
    const { t } = useTranslation();
    countriesList.registerLocale(countriesJson);
    const resolvedFarms = useAsyncValue().message.map((farm) => {
        if (farm.new_crop !== null) {
            farm.old_crop = null;
            farm.crops = farm.new_crop;
        } else if ( farm.old_crop !== null && farm.old_crop[0] !== "") {
            farm.new_crop = null;
            farm.crops = farm.old_crop;
        } else {
            farm.new_crop = null;
            farm.old_crop = null;
            farm.crops = [t("map.noDeclaredCrop")];
        }
        if ( farm.dist === null) {
            farm.dist = [""];
        }
        if ( farm.centro_costo === null) {
            farm.centro_costo = "";
        }
        return farm;
    });
    useEffect(() => {
        setFarmsSelected(resolvedFarms);
    },[]);
    const [accountSelectValue, setAccountSelectValue] = useState([]);
    const [sizeSelectValue, setSizeSelectValue] = useState([]);
    const [cropSelectValue, setCropSelectValue] = useState([]);
    const [countrySelectValue, setCountrySelectValue] = useState([]);
    const [integratorSelectValue, setIntegratorSelectValue] = useState([]);
    const [costCenterSelectValue, setCostCenterSelectValue] = useState([]);

    const Accounts = [...new Set(resolvedFarms.map((farm) => farm.account_name))].sort();
    const AccountOptions = Accounts.map((account) => {
        return (
            {
                label: account,
            }
        );
    });

    const sizeOptions = [
        { label: "0 - 5", lowerBound: 0, upperBound: 5 },
        { label: "6 - 10", lowerBound: 6, upperBound: 10 },
        { label: "11 - 20", lowerBound: 11, upperBound: 20 },
        { label: "21 - 30", lowerBound: 21, upperBound: 30 },
        { label: "31 - 40", lowerBound: 31, upperBound: 40 },
        { label: "41 - 50", lowerBound: 41, upperBound: 50 },
        { label: "51 >", lowerBound: 51, upperBound: Number.MAX_SAFE_INTEGER }];

    let old_crops = resolvedFarms.filter((farm) => {
        if (farm.old_crop) {
            return true;
        } else {
            return false;
        }
    }).map((farm) => farm.old_crop).flat(1).sort();

    let new_crops = resolvedFarms.filter((farm) => {
        if (farm.new_crop) {
            return true;
        } else {
            return false;
        }
    }).map((farm) => farm.new_crop).flat(1).sort();

    const CropOptions = [t("map.noDeclaredCrop")].concat([...new Set(new_crops)]).map((crop) => {
        return (
            {
                label: crop,
                value : crop
            }
        );
    }).concat([...new Set(old_crops)].map((crop) => {
        return (
            {
                label: `${crop} ${t("map.oldVersion")}`,
                value: crop
            }
        );
    }));

    const Countries = [...new Set(resolvedFarms.map((farm) => farm.country_code))];
    const countryOptions = Countries.map((countryCode) => {
        return (
            {
                label: countriesList.getName(countryCode, "en"),
                value: countryCode
            }
        );
    });
    countryOptions.sort(function(a, b) {
        let textA = a.label.toUpperCase();
        let textB = b.label.toUpperCase();
        return textA.localeCompare(textB);
    });

    const Integrators = [...new Set(resolvedFarms.map((farm) => farm.dist).flat(1))].sort();
    const integratorOptions = Integrators.map((integrator) => {
        return (
            {
                label: integrator,
            }
        );
    });

    const costCenters = [...new Set(resolvedFarms.map((farm) => farm.centro_costo))].sort();
    const costCenterOptions = costCenters.map((costCenter) => {
        return (
            {
                label: costCenter,
            }
        );
    });

    const displayOptions = [
        { label: t("map.map") },
        { label: t("map.table") },
        { label: t("map.summary") }
    ];
    const [countryFarmsSelection, setCountryFarmsSelection] = useState([]);
    const [accountFarmsSelection, setAccountFarmsSelection] = useState([]);
    const [sizeFarmsSelection, setSizeFarmsSelection] = useState([]);
    const [cropFarmsSelection, setCropFarmsSelection] = useState([]);
    const [integratorFarmsSelection, setIntegratorFarmsSelection] = useState([]);
    const [costCenterFarmsSelection, setCostCenterFarmsSelection] = useState([]);

    useEffect(() => {
        if (countrySelectValue.length>0) {
            setCountryFarmsSelection(resolvedFarms.filter((farm) => countrySelectValue.includes(farm.country_code)));
        } else {
            setCountryFarmsSelection(resolvedFarms);
        }
    }, [countrySelectValue]);

    useEffect(() => {
        if (accountSelectValue.length>0) {
            const selectedAccounts = accountSelectValue.map((account) => account.label);
            setAccountFarmsSelection(resolvedFarms.filter((farm) => selectedAccounts.includes(farm.account_name)));
        } else {
            setAccountFarmsSelection(resolvedFarms);
        }
    }, [accountSelectValue]);

    useEffect(() => {
        if (sizeSelectValue.length>0) {
            setSizeFarmsSelection(resolvedFarms.filter((farm) => {
                for (let i = 0; i < sizeSelectValue.length; i++) {
                    if (farm.nodes >= sizeSelectValue[i].lowerBound && farm.nodes <= sizeSelectValue[i].upperBound) {
                        return true;
                    }
                }
                return false;
            }));
        } else {
            setSizeFarmsSelection(resolvedFarms);
        }
    }, [sizeSelectValue]);

    useEffect(() => {
        if (cropSelectValue.length>0) {
            setCropFarmsSelection(resolvedFarms.filter((farm) => {
                const contains = cropSelectValue.some(crop => {
                    return farm.crops.includes(crop);
                });
                if (contains) {
                    return true;
                }
                return false;
            }));
        } else {
            setCropFarmsSelection(resolvedFarms);
        }
    }, [cropSelectValue]);

    useEffect(() => {
        if (integratorSelectValue.length>0) {
            const selectedIntegrator = integratorSelectValue.map((account) => account.label);
            setIntegratorFarmsSelection(resolvedFarms.filter((farm) => {
                const contains = selectedIntegrator.some(integrator => {
                    return farm?.dist?.includes(integrator);
                });
                if (contains) {
                    return true;
                }
                return false;
            }));
        } else {
            setIntegratorFarmsSelection(resolvedFarms);
        }
    }, [integratorSelectValue]);

    useEffect(() => {
        if (costCenterSelectValue.length>0) {
            const selectedCostCenter = costCenterSelectValue.map((costCenter) => costCenter.label);
            setCostCenterFarmsSelection(resolvedFarms.filter((farm) => selectedCostCenter.includes(farm.centro_costo)));
        } else {
            setCostCenterFarmsSelection(resolvedFarms);
        }
    }, [costCenterSelectValue]);



    useEffect(() => {
        setFarmsSelected(resolvedFarms.filter((farm) => countryFarmsSelection.includes(farm) &&
            accountFarmsSelection.includes(farm) && sizeFarmsSelection.includes(farm)
            && cropFarmsSelection.includes(farm) && integratorFarmsSelection.includes(farm)
            && costCenterFarmsSelection.includes(farm)));
    }, [countryFarmsSelection, accountFarmsSelection, sizeFarmsSelection, cropFarmsSelection, integratorFarmsSelection, costCenterFarmsSelection]);


    const itemTemplate = (option) => {
        return (
            <div classlabel="flex align-items-center">
                <span className="ml-2">
                    {option.label}
                </span>
            </div>
        );
    };

    const [optionsOpen, setOptionsOpen] = useState(false);
    const [showDisplaySelector, setShowDisplaySelector] = useState(false);
    const delay = 600;

    useEffect(()=>{
        if(optionsOpen === false){
            setTimeout(()=>{
                setShowDisplaySelector(false);
            }, delay);
        }
    },[optionsOpen]);
    return (
        <div className="grid flex align-items-center">
            <Accordion
                className={optionsOpen ? "col-12 transition-all transition-duration-500 transition-delay-500" : "col-10 transition-all transition-duration-500" }
                onTabOpen={()=>{
                    setOptionsOpen(true);
                    setShowDisplaySelector(true);
                }}
                onTabClose={()=>{
                    setOptionsOpen(false);
                }}
            >
                <AccordionTab header={
                    <div>
                        {!optionsOpen ? accountSelectValue.map((account) => {
                            return (
                                <Chip
                                    key={account.label} className="accountChip" label={account.label} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setAccountSelectValue(accountSelectValue.filter(filterAccount => filterAccount.label != account.label));
                                    }}
                                />
                            );
                        }) : null}
                        {!optionsOpen ? sizeSelectValue.map((size) => {
                            return (
                                <Chip
                                    key={size.label} className="sizeChip" label={size.label} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSizeSelectValue(sizeSelectValue.filter(filterSize => filterSize.label != size.label));
                                    }}
                                />
                            );
                        }) : null}
                        {!optionsOpen ? cropSelectValue.map((crop) => {
                            return (
                                <Chip
                                    key={crop} className="cropChip" label={crop} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCropSelectValue(cropSelectValue.filter(filterCrop => filterCrop != crop));
                                    }}
                                />
                            );
                        }) : null}
                        {!optionsOpen ? countrySelectValue.map((country) => {
                            return (
                                <Chip
                                    key={country} className="countryChip" label={countriesList.getName(country, "en")} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCountrySelectValue(countrySelectValue.filter(filterCountry => filterCountry != country));
                                    }}
                                />
                            );
                        }) : null}
                        {!optionsOpen ? integratorSelectValue.map((integrator) => {
                            return (
                                <Chip
                                    key={integrator.label} className="integratorChip" label={integrator.label} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIntegratorSelectValue(integratorSelectValue.filter(filterIntegrator => filterIntegrator.label != integrator.label));
                                    }}
                                />
                            );
                        }) : null}
                        {!optionsOpen ? costCenterSelectValue.map((costCenter) => {
                            return (
                                <Chip
                                    key={costCenter.label} className="costCenterChip" label={costCenter.label} removable onRemove={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCostCenterSelectValue(costCenterSelectValue.filter(filterCostCenter => filterCostCenter.label != costCenter.label));
                                    }}
                                />
                            );
                        }) : null}
                        {accountSelectValue.length === 0 && sizeSelectValue.length === 0
                        && cropSelectValue.length === 0 && countrySelectValue.length === 0
                        && integratorSelectValue.length === 0 && costCenterSelectValue.length === 0
                        || optionsOpen  ? t("map.options") : null}
                    </div>
                }
                >
                    <div className="grid p-fluid">
                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.account")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={accountSelectValue}
                                    onChange={(e) => setAccountSelectValue(e.value)}
                                    options={AccountOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectAccountPlaceholder")}
                                    filter
                                    className="multiselect-custom accountChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.farmSize")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={sizeSelectValue}
                                    onChange={(e) => setSizeSelectValue(e.value)}
                                    options={sizeOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectFarmSizePlaceholder")}
                                    className="multiselect-custom sizeChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.crop")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={cropSelectValue}
                                    onChange={(e) => setCropSelectValue(e.value)}
                                    options={CropOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectCropPlaceholder")}
                                    filter
                                    className="multiselect-custom cropChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.country")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={countrySelectValue}
                                    onChange={(e) => setCountrySelectValue(e.value)}
                                    options={countryOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectCountryPlaceholder")}
                                    filter
                                    className="multiselect-custom countryChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.integrator")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={integratorSelectValue}
                                    onChange={(e) => setIntegratorSelectValue(e.value)}
                                    options={integratorOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectIntegratorPlaceholder")}
                                    filter
                                    className="multiselect-custom integratorChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.costCenter")}
                            </h6>
                            <div className="p-inputgroup">
                                <MultiSelect
                                    value={costCenterSelectValue}
                                    onChange={(e) => setCostCenterSelectValue(e.value)}
                                    options={costCenterOptions}
                                    itemTemplate={itemTemplate}
                                    optionLabel="label"
                                    placeholder={t("map.selectCostCenterPlaceholder")}
                                    filter
                                    className="multiselect-custom costCenterChip"
                                    display="chip"
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <h6>
                                {t("map.display")}
                            </h6>
                            <div className="p-inputgroup">
                                <SelectButton
                                    value={selectDisplay}
                                    onChange={(e) => {
                                        if(e.value !== null){
                                            setselectDisplay(e.value);
                                        }
                                    }}
                                    options={displayOptions}
                                    optionLabel="label"
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
            <Dropdown className={showDisplaySelector ? "col-2 h-4rem text-center hidden transition-all transition-duration-500 fadeout" : "col-2 h-4rem text-center transition-all transition-duration-500 fadein" } value={selectDisplay} options={displayOptions} onChange={(e) => setselectDisplay(e.value)} optionLabel="label" />
        </div>
    );
};

const LoadingMapOptions = () => {
    const { t } = useTranslation();
    return (
        <div className="grid flex align-items-center">
            <Accordion
                className="col-10"

            >
                <AccordionTab header={t("map.loadingData")} disabled />
            </Accordion>
            <Dropdown className="col-2 h-4rem" disabled />
        </div>
    );
};

export { MapOptions, LoadingMapOptions };
